import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types-expenses-flat-rate';

export type FlatRateSetting = components['schemas']['FlatRateSetting'];
/** Decision 197: what switching the mode sweeps in the notes not yet approved. */
export type FlatRateSweep = components['schemas']['FlatRateSweep'];
export type FlatRateSaved = FlatRateSetting & { readonly swept: FlatRateSweep };

/** What a flat-rate call came to: the value, or the HTTP status and the problem's `code`. */
export type Outcome<T> =
  | { readonly ok: true; readonly value: T }
  | {
      readonly ok: false;
      readonly status: number;
      readonly code: string;
      /** The problem's body, for the members a code carries (`SWEEP_CHANGED`'s new numbers). */
      readonly problem?: Readonly<Record<string, unknown>>;
    };

type Run<T> = () => Promise<{ data?: T; error?: unknown; response: Response }>;

function codeOf(error: unknown): string {
  return typeof error === 'object' && error !== null && 'code' in error ? String(error.code) : '';
}

async function call<T>(run: Run<T>): Promise<Outcome<T>> {
  try {
    const { data, error, response } = await run();
    if (response.ok) return { ok: true, value: data as T };
    const failed = { ok: false as const, status: response.status, code: codeOf(error) };
    return typeof error === 'object' && error !== null
      ? { ...failed, problem: error as Record<string, unknown> }
      : failed;
  } catch {
    return { ok: false, status: 0, code: '' };
  }
}

export function getFlatRateSetting(): Promise<Outcome<FlatRateSetting>> {
  return call(() => apiClient.GET('/v1/expenses/settings/flat-rate'));
}

/** How many notes and lines saving `body` would change; the server writes nothing. */
export function previewFlatRateSetting(body: FlatRateSetting): Promise<Outcome<FlatRateSweep>> {
  return call(() => apiClient.POST('/v1/expenses/settings/flat-rate/preview', { body }));
}

/** The counts the person said yes to; the server sweeps only if it finds the same. */
export type SweepConfirmation = { readonly notes: number; readonly lines: number };

/** `confirm` only once the person said yes to the sweep the preview announced. */
export function putFlatRateSetting(
  body: FlatRateSetting,
  confirm?: SweepConfirmation,
): Promise<Outcome<FlatRateSaved>> {
  return call(() =>
    apiClient.PUT('/v1/expenses/settings/flat-rate', {
      body: confirm ? { ...body, confirm: { notes: confirm.notes, lines: confirm.lines } } : body,
    }),
  );
}

/** `SWEEP_CHANGED`'s numbers: what the sweep finds now, to confirm again. */
export function sweepOfProblem(
  problem: Readonly<Record<string, unknown>> | undefined,
): FlatRateSweep | null {
  const { sweep, notes, lines } = problem ?? {};
  return (sweep === 'remove' || sweep === 'add' || sweep === 'none') &&
    typeof notes === 'number' &&
    typeof lines === 'number'
    ? { sweep, notes, lines }
    : null;
}

/** Decision 203: Gestione or an approver takes one line out, with the required reason. */
export function removeAllowanceLine(
  reportId: string,
  expenseId: string,
  reason: string,
): Promise<Outcome<unknown>> {
  return call<unknown>(() =>
    apiClient.DELETE('/v1/expenses/reports/{reportId}/flat-rate/{expenseId}', {
      params: { path: { reportId, expenseId } },
      body: { reason },
    }),
  );
}
